import { useState } from "react";
import { useSortable } from "@dnd-kit/sortable";
import { Stack, TextField, Typography } from "@mui/material";
import { CSS } from "@dnd-kit/utilities";
import {
  CheckIcon,
  CloseIcon,
  DragFieldIcon,
  PenIcon,
  StarIcon,
  StarWithBorderIcon,
} from "../../../../images";
import { CustomButton } from "../../../../ui/CustomButton";
import { DeleteImg } from "../../../../images/deleteImg";
import { JobTitle } from "../../../../../types/apiTypes";
import { SyntheticListenerMap } from "@dnd-kit/core/dist/hooks/utilities";
import { DraggableAttributes } from "@dnd-kit/core";
import { ReuseModal } from "../../../../ui";
import { EllipsisTooltip } from "../../../../ui/CustomTooltip";

const EditItem = ({
  withHandle,
  isDragging,
  changeHandler,
  item,
  changeEdit,
  removeHandler,
  isSearchItem,
}: {
  index: number;
  withHandle: boolean;
  changeHandler: (item: JobTitle) => void;
  isDragging: boolean;
  item: JobTitle;
  changeEdit: () => void;
  removeHandler: (id: string) => void;
  isSearchItem: boolean;
}) => {
  const [currentTitle, setCurrentTitle] = useState<string>(item.title);

  const changeTitleHandler = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    setCurrentTitle(e.target.value);
  };

  const submitChanges = () => {
    changeHandler({
      ...item,
      title: currentTitle,
      is_edit: false,
      is_suggest: false,
    });
  };

  const cancelHandler = () => {
    if (!item.title || item.is_suggest) return removeHandler(item.id);
    changeEdit();
  };

  const isSuggest = item.is_suggest;

  return (
    <Stack flexDirection="row" gap="4px" flexGrow={1}>
      <Stack
        sx={{
          padding: "8px 4px",
          border: "1px solid #F1F1F1",
          borderRadius: "8px",
          backgroundColor: isDragging ? "#f0f0f0" : "#fff",
          flexDirection: "row",
          alignItems: "center",
          flexGrow: 1,
          borderLeft: `1px solid ${!withHandle ? "#0860A8CC" : "#F1F1F1"}`,
          gap: "4px",
        }}
        style={{
          ...(isSuggest && { backgroundColor: "#FEF0E6" }),
          ...(isSearchItem && { border: "2px solid #1C3C6C33" }),
        }}
      >
        <Stack
          sx={{
            flexDirection: "row",
            alignItems: "center",
            gap: "4px",
            flexGrow: 1,
          }}
        >
          <DragFieldIcon />
          {isSuggest ? (
            <Typography
              component="span"
              variant="subtitle1"
              color="custom.textDarkBlue"
              sx={{
                display: "-webkit-box",
                WebkitLineClamp: 2,
                WebkitBoxOrient: "vertical",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {item.title}
            </Typography>
          ) : (
            <TextField
              value={currentTitle}
              variant="outlined"
              placeholder="Enter 3 - 100 alphabetic characters"
              onChange={changeTitleHandler}
              sx={{
                width: "100%",
                color: "#1F2932",
                borderRadius: "8px",
                textAlign: "center",
                "& .MuiOutlinedInput-root": {
                  padding: 0,
                },
                "& input": {
                  padding: "0",
                },
              }}
            />
          )}
        </Stack>
        <Stack flexDirection="row" flexWrap="nowrap">
          <CustomButton
            variant="outlined"
            customColor=""
            customSize="s"
            onClick={cancelHandler}
            sx={{
              minWidth: "auto",
              p: 1,
              width: "42px",
              height: "42px",
              border: "none",
              "&:hover": {
                backgroundColor: "rgba(28, 60, 108, 0.08)",
              },
            }}
          >
            <CloseIcon color={isSuggest ? "#BD4D00" : undefined} />
          </CustomButton>
          <CustomButton
            variant="outlined"
            customColor=""
            customSize="s"
            onClick={submitChanges}
            sx={{
              minWidth: "auto",
              p: 1,
              width: "42px",
              height: "42px",
              border: "none",
              "&:hover": {
                backgroundColor: "rgba(28, 60, 108, 0.08)",
              },
            }}
          >
            <CheckIcon color={isSuggest ? "#BD4D00" : undefined} />
          </CustomButton>
        </Stack>
      </Stack>
    </Stack>
  );
};

const Item = ({
  index,
  withHandle,
  isDragging,
  toogleCrirtical,
  item,
  changeEdit,
  removeHandler,
  listeners,
  attributes,
  isSearchItem,
}: {
  index: number;
  withHandle: boolean;
  toogleCrirtical: (item: JobTitle, index: number) => void;
  isDragging: boolean;
  item: JobTitle;
  changeEdit: () => void;
  removeHandler: (id: string) => void;
  listeners: SyntheticListenerMap | undefined;
  attributes: DraggableAttributes;
  isSearchItem: boolean;
}) => {
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState<boolean>(false);

  return (
    <Stack flexDirection="row" gap="4px" flexGrow={1}>
      <Stack
        sx={{
          padding: "8px 4px",
          border: isSearchItem ? "2px solid #1C3C6C33" : "1px solid #F1F1F1",
          borderRadius: "8px",
          backgroundColor: isDragging ? "#f0f0f0" : "#fff",
          flexDirection: "row",
          alignItems: "center",
          flexGrow: 1,
          borderLeft: isSearchItem
            ? "2px solid #1C3C6C33"
            : `1px solid ${!withHandle ? "#0860A8CC" : "#F1F1F1"}`,
        }}
      >
        <Stack
          sx={{
            cursor: "grab",
            flexDirection: "row",
            alignItems: "center",
            gap: "4px",
            flexGrow: 1,
          }}
          {...listeners}
          {...attributes}
        >
          <DragFieldIcon />
          <Typography
            component="span"
            variant="subtitle1"
            color="custom.textDarkBlue"
            sx={{
              display: "-webkit-box",
              WebkitLineClamp: 2,
              WebkitBoxOrient: "vertical",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {item.title}
          </Typography>
        </Stack>
        <Stack flexDirection="row" gap="8px">
          <Stack
            onClick={() => toogleCrirtical(item, index)}
            sx={{
              justifyContent: "center",
              alignItems: "center",
              cursor: "pointer",
            }}
          >
            <EllipsisTooltip
              key={JSON.stringify(item)}
              tooltip="Mark as critical"
            >
              {item.is_critical ? <StarIcon /> : <StarWithBorderIcon />}
            </EllipsisTooltip>
          </Stack>
          <Stack flexDirection="row" flexWrap="nowrap">
            <CustomButton
              variant="outlined"
              customColor=""
              customSize="s"
              onClick={() => changeEdit()}
              sx={{
                minWidth: "auto",
                p: 1,
                width: "42px",
                height: "42px",
                border: "none",
                "&:hover": {
                  backgroundColor: "rgba(28, 60, 108, 0.08)",
                },
              }}
            >
              <PenIcon />
            </CustomButton>
            <CustomButton
              variant="outlined"
              customColor=""
              customSize="s"
              onClick={() => setIsDeleteModalOpen(true)}
              sx={{
                minWidth: "auto",
                p: 1,
                width: "42px",
                height: "42px",
                border: "none",
                "&:hover": {
                  backgroundColor: "rgba(28, 60, 108, 0.08)",
                },
              }}
            >
              <DeleteImg />
            </CustomButton>
          </Stack>
        </Stack>
      </Stack>
      <ReuseModal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        onSubmit={() => removeHandler(item.id)}
        title="Delete Job Title?"
        text="Are you sure you want to delete this job title? "
        maxWidth="430px"
        submitBtnColor="#C0392B"
        submitBtnText="Delete"
      />
    </Stack>
  );
};

export const JobTitleItem = ({
  item,
  index,
  withHandle = true,
  toogleCrirtical,
  changeHandler,
  removeHandler,
  searchItemId,
}: {
  item: JobTitle;
  withHandle?: boolean;
  index: number;
  toogleCrirtical: (item: JobTitle, index: number) => void;
  changeHandler: (item: JobTitle, onSuccess?: () => void) => void;
  removeHandler: (id: string) => void;
  searchItemId?: string;
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: item.id });

  return (
    <Stack
      id={item.id}
      component="li"
      ref={setNodeRef}
      sx={{
        alignItems: "center",
        flexDirection: "row",
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.3 : 1,
        flexGrow: 1,
        width: "100%",
      }}
    >
      <Typography
        variant="subtitle1"
        color="custom.textDarkBlue"
        minWidth="32px"
      >
        {withHandle && index + 1}
      </Typography>

      {item.is_edit ? (
        <EditItem
          index={index}
          changeEdit={() => changeHandler({ ...item, is_edit: !item.is_edit })}
          item={item}
          withHandle={withHandle}
          changeHandler={changeHandler}
          isDragging={isDragging}
          removeHandler={removeHandler}
          isSearchItem={searchItemId === item.id}
        />
      ) : (
        <Item
          index={index}
          changeEdit={() => changeHandler({ ...item, is_edit: !item.is_edit })}
          item={item}
          listeners={listeners}
          attributes={attributes}
          withHandle={withHandle}
          toogleCrirtical={toogleCrirtical}
          isDragging={isDragging}
          removeHandler={removeHandler}
          isSearchItem={searchItemId === item.id}
        />
      )}
    </Stack>
  );
};
